import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState, AppActionCreators } from '../store';
import { decodeJwtPayload } from './useRefreshToken';

const getTokenIssuedAt = (token: string | null | undefined): number => {
  if (!token) {
    return 0;
  }

  const decoded = decodeJwtPayload(token);

  return decoded && decoded.iat ? decoded.iat : 0;
};

export const useCrossTabAuth = () => {
  const dispatch = useDispatch();
  const currentToken = useSelector((state: ApplicationState) => state.app?.token);
  const currentRefreshToken = useSelector((state: ApplicationState) => state.app?.refreshToken);
  const currentUser = useSelector((state: ApplicationState) => state.app?.appUser);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (isProcessingRef.current) return;
      if (event.key !== 'access_token') return;

      isProcessingRef.current = true;

      try {
        if (!event.newValue) {
          if (currentUser || currentToken) {
            (dispatch as any)(AppActionCreators.LogoutCredentials());
            window.location.href = '/auth/login';
          }
          return;
        }

        if (!currentToken && !currentUser) {
          window.location.reload();
          return;
        }

        if (event.newValue !== currentToken) {
          // Adopt ONLY a fresher token: a stale tab can rewrite storage with its old (already rotated) token - blind adoption would poison the current tab and the next refresh would fail with invalid_grant.
          const currentIssuedAt = getTokenIssuedAt(currentToken);
          const newIssuedAt = getTokenIssuedAt(event.newValue);

          if (newIssuedAt >= currentIssuedAt) {
            (dispatch as any)(AppActionCreators.SetToken(event.newValue));
            const newRefresh = localStorage.getItem('refresh_token');
            if (newRefresh) {
              (dispatch as any)(AppActionCreators.SetRefreshToken(newRefresh));
            }
          } else if (currentToken) {
            // Storage was overwritten with a state older than ours - repair it.
            localStorage.setItem('access_token', currentToken);
            if (currentRefreshToken) {
              localStorage.setItem('refresh_token', currentRefreshToken);
            }
          }
        }
      } finally {
        isProcessingRef.current = false;
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [dispatch, currentUser, currentToken, currentRefreshToken]);

  return null;
};

export default useCrossTabAuth;
